import { ButtonSecondary, Colors, Container, Icon, Text } from '@amelio/ui-library';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { EmptyPeriodResults } from './components/EmptyPeriodResults/EmptyPeriodResults';
import { Results } from './components/Results';

import { useGetUsersByIds } from '@/api/directory';
import { useCreateCommentReply } from '@/api/review-services/comment-reply';
import { useGetReviewInstance } from '@/api/review-services/review-instance';
import { useGetLatestReviewInstanceIdByPeriod } from '@/api/review-services/review-instance';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { type ReviewTypeValue } from '@/constants/review/CycleInstance';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import {
    getAllReviewers,
    getAllReviewInstanceAssignedUserIds,
    getReviewQuestionAnswers,
    getReviewee,
} from '@/utils/reviewInstance';

export const ResultsPage = () => {
    const { reviewInstanceId, employeeId, reviewType } = useParams<{
        reviewInstanceId?: string;
        employeeId?: string;
        reviewType?: ReviewTypeValue;
    }>();
    const location = useLocation();
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();

    // Determine if we're in period view or single instance view
    const isPeriodView = !reviewInstanceId && employeeId && reviewType;

    const handleBack = () => {
        navigate({ path: RouteName.Evaluation360 });
    };

    // Parse dates for period view
    const searchParams = new URLSearchParams(location.search);
    const startDate = searchParams.get('startDate') || '';
    const endDate = searchParams.get('endDate') || '';

    // Step 1: Get reviewInstanceId (either from URL or from API)
    const {
        data: latestReviewInstanceId,
        isLoading: isLatestIdLoading,
        isError: isLatestIdError,
    } = useGetLatestReviewInstanceIdByPeriod(
        employeeId || '',
        reviewType as ReviewTypeValue,
        startDate,
        endDate,
    );

    // Step 2: Get full review instance data
    const effectiveReviewInstanceId = isPeriodView
        ? latestReviewInstanceId || ''
        : reviewInstanceId || '';

    const {
        data: reviewInstance,
        isLoading: isInstanceLoading,
        isError: isInstanceError,
    } = useGetReviewInstance(effectiveReviewInstanceId);

    const isLoading = isLatestIdLoading || isInstanceLoading;
    const isError = isLatestIdError || isInstanceError;
    const effectiveReviewInstance = reviewInstance;

    const { mutateAsync: createCommentReply, isPending: createCommentReplyIsPending } =
        useCreateCommentReply(effectiveReviewInstanceId);

    const userIds = useMemo(() => {
        if (!effectiveReviewInstance) return [];
        return getAllReviewInstanceAssignedUserIds([effectiveReviewInstance]);
    }, [effectiveReviewInstance]);

    const { data: users, isLoading: isUsersLoading } = useGetUsersByIds({
        userIds,
        identifier: `results-${effectiveReviewInstanceId}`,
    });
    const usersList = users || [];

    const reviewee = getReviewee({ reviewInstance: effectiveReviewInstance, users: usersList });

    const reviewers = getAllReviewers({
        reviewInstance: effectiveReviewInstance,
        users: usersList,
    });

    const questions = useMemo(
        () =>
            getReviewQuestionAnswers({ reviewInstance: effectiveReviewInstance, users: usersList }),
        [effectiveReviewInstance, usersList],
    );

    const handleReplySubmit = async (reviewQuestionAnswerId: string, content: string) => {
        try {
            await createCommentReply({
                reviewQuestionAnswerId,
                content,
            });
        } catch (error) {
            handleError({ error });
        }
    };

    useFetchErrorsHandler({
        errors: [isError],
    });

    if (isLoading || isUsersLoading) {
        return (
            <Container
                direction={'col'}
                p={'md'}
                className={'w-full'}
                dataTestId={'results-skeleton'}
            >
                <Skeleton>
                    <div className={'space-y-6'}>
                        <div className={'flex items-center gap-4'}>
                            <Bone className={'h-20 w-20 rounded-full'} />

                            <div className={'space-y-2'}>
                                <Bone className={'h-7.5 w-50'} />

                                <Bone className={'h-5 w-37.5'} />
                            </div>
                        </div>

                        <Bone className={'h-50 w-full'} />

                        <div className={'space-y-10'}>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={'space-y-4'}
                                >
                                    <Bone className={'h-6.25 w-3/5'} />

                                    <Bone className={'h-37.5 w-full'} />
                                </div>
                            ))}
                        </div>
                    </div>
                </Skeleton>
            </Container>
        );
    }

    // Show no evaluation available message when period view has no results
    if (isPeriodView && !latestReviewInstanceId && !isLatestIdLoading) {
        return <EmptyPeriodResults />;
    }

    if (isError || !effectiveReviewInstance || !users || !reviewee || !reviewers || !questions) {
        return (
            <Container
                direction={'col'}
                p={'xl'}
                className={'w-full items-center justify-center'}
                dataTestId={'results-error'}
            >
                <Text
                    size={'large'}
                    color={Colors.ERROR}
                    weight={'semibold'}
                >
                    {t('questionResult.errors.fetchResultsFailed')}
                </Text>
            </Container>
        );
    }

    return (
        <Container
            direction={'col'}
            className={'results-layout flex-1 justify-between'}
        >
            <Results
                reviewInstance={effectiveReviewInstance}
                reviewee={reviewee}
                reviewers={reviewers}
                questions={questions}
                onReplySubmit={handleReplySubmit}
                isSubmittingReply={createCommentReplyIsPending}
            />

            <Container
                direction={'row'}
                className={'mt-4 flex-1 items-end justify-end'}
                p={'lg'}
            >
                <ButtonSecondary
                    className={'mr-2'}
                    dataTestId={'back-button'}
                    onClick={handleBack}
                    leftIcon={
                        <Icon
                            name={'arrow-arrow-left'}
                            className={'mr-2'}
                        />
                    }
                >
                    {t('common.back')}
                </ButtonSecondary>
            </Container>
        </Container>
    );
};

export default ResultsPage;
